/**
 * Member kinds (spec 2026-10-06-04). Every membership is `staff`, `resident`
 * or `provider`, and the kind bounds the roles it may hold. The backend
 * enforces the rules (internal/authz/kinds.go); this mirrors them so the UI
 * only offers what would be accepted.
 */
export type MemberKind = 'staff' | 'resident' | 'provider'

export const MEMBER_KINDS: readonly MemberKind[] = ['staff', 'resident', 'provider']

export const GUEST_ROLE = 'guest'

/** The grant roles of each external kind: built-in ones only. */
const KIND_GRANT_ROLES: Record<Exclude<MemberKind, 'staff'>, readonly string[]> = {
    resident: ['resident', 'property_owner'],
    provider: ['provider'],
}

export function isMemberKind(value: unknown): value is MemberKind {
    return typeof value === 'string' && (MEMBER_KINDS as readonly string[]).includes(value)
}

/**
 * roleFitsKind mirrors `authz.GrantRoleFitsKind` for the invite dialog: staff
 * are invited with an organization-wide role (never a scoped-only one),
 * residents with `resident` or `property_owner`, providers with `provider`. A
 * custom scoped-only role fits no kind.
 */
export function roleFitsKind(
    kind: MemberKind,
    role: { code: string; scoped_only?: boolean | null }
): boolean {
    if (kind === 'staff') return !role.scoped_only

    return KIND_GRANT_ROLES[kind].includes(role.code)
}

/** rolesForKind keeps the roles a member of that kind may be invited with. */
export function rolesForKind<R extends { code: string; scoped_only?: boolean | null }>(
    kind: MemberKind,
    roles: readonly R[]
): R[] {
    return roles.filter((role) => roleFitsKind(kind, role))
}

export type OrgHome = 'loading' | 'my-places' | 'manager'

/**
 * homeForKind picks the organization home: "My places" for residents and
 * providers, the manager home for staff, including a staff member narrowed
 * by grants, such as an agency director. While the membership summary loads
 * the kind is unknown and the answer is `loading`: the page renders a
 * placeholder rather than the manager home, which would flash to a resident
 * and fire requests meant for staff. Once settled, a missing kind (no
 * membership, an unknown value) falls back to the manager home, whose data
 * the backend gates anyway.
 */
export function homeForKind(kind: MemberKind | undefined, isLoading = false): OrgHome {
    if (kind === 'resident' || kind === 'provider') return 'my-places'
    if (kind === undefined && isLoading) return 'loading'

    return 'manager'
}
